import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useQuery } from '@apollo/client';
import { Redirect } from 'react-router-dom';

import * as K from 'common/constants';
import { ALERT_QUERY } from 'components/alerts/queries';
import ELEMENT from 'common/element';
import { parseDateTime, timezoneMoment } from 'common/util/time';
import { buildUrl } from 'common/util/location';
import { TimeRange, UrlParams } from 'common/types';
import Link from 'components/common/Link/Link';
import Page from 'components/common/Page/Page';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import TabbedPageHeader from 'components/common/TabbedPageHeader/TabbedPageHeader';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import AlertDetailsToolbar from 'components/alerts/AlertDetailsPage/AlertDetailsToolbar';
import TimeSeriesGraphContainer from 'components/alerts/AlertDetailsPage/TimeSeriesGraphContainer';

import './AlertDetailsPage.scss';

const DEFAULT_FACET = 'value';

/**
 * The page that contains details of an alert.
 */

function AlertDetailsPage(): JSX.Element {
  const alertId = useRecoilValue(searchParamState(K.URL_PARAM.ID));
  const timezone = useRecoilValue(timezoneState);
  const { loading, error, data } = useQuery(ALERT_QUERY, {
    variables: {
      [K.URL_PARAM.ID]: alertId,
    },
  });
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);

  useEffect(() => {
    const startTime = timezoneMoment(timezone)().subtract(1, 'hour').format('x');
    const endTime = timezoneMoment(timezone)().format('x');

    setTimeRange({
      startTime,
      endTime,
    });
  }, []);

  if (loading) {
    return <LoadingPlaceholder />;
  }

  if (!data.alert) {
    return <Redirect to={K.ROUTE.ALERTS} />;
  }

  if (error) {
    return <ErrorPlaceholder error={error} />;
  }

  const { alert } = data;
  const getMuteDate = (): string | null => {
    if (alert.silencedUntilNs) {
      const parsedDate = parseDateTime(alert.silencedUntilNs, null, null, timezone);
      return parsedDate ? parsedDate.moment.format('MMM D · hh:mm a') : null;
    }

    return null;
  };

  const gracePeriodNotification =
    alert.gracePeriod === 0 ? "Don't wait before notifying" : `Wait ${alert.gracePeriod} minutes before notifying`;

  const reminderPeriodNotification =
    alert.renotifyPeriod > 0
      ? `Do not remind me until triggered for another ${alert.renotifyPeriod} minutes`
      : 'Reminder period deactivated';

  const resolutionDelayNotification =
    alert.resolutionDelay !== 9999
      ? `Wait ${alert.resolutionDelay} minutes before notifying of any resolution`
      : 'Resolution delay deactivated';

  const shouldShowFirstSection = alert.description || alert.silenced;

  const isCount = alert.trigger?.startsWith(K.RATE.name);
  const facet = alert.parsedTriggerExpressions?.[0]?.facet || DEFAULT_FACET;
  const { fn } = alert.parsedTriggerExpressions?.[0] || {};

  let facetValue = isCount ? '' : facet;
  if (fn?.operator && fn?.operator !== K.RATE.name) {
    if (fn?.operator === 'p') {
      facetValue = `${fn.operator}[${fn.param * 100}](${facet})`;
    } else {
      facetValue = `${fn.operator}(${facet})`;
    }
  }

  // TODO: fix barWidth calculation {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-6955}
  // const barWidth = alert.trigger.split(':')?.[1].split('(')?.[0] || '1 minute';
  const barWidth = '1 minute';

  const expandTimeSeriesLink = buildUrl(K.ROUTE.EVENTS, {
    [K.URL_PARAM.FILTER]: alert.parsedTriggerExpressions?.[0]?.filter,
    [K.URL_PARAM.BAR_WIDTH]: barWidth,
    [K.URL_PARAM.GRAPH_STYLE]: K.CHART_TYPES.STACKED_BAR,
    [K.URL_PARAM.MODE]: K.EVENT_MODE.GRAPH,
    [K.URL_PARAM.START_TIME]: timeRange?.startTime,
    [K.URL_PARAM.END_TIME]: timeRange?.endTime,
    [K.URL_PARAM.FACET]: facetValue,
  } as UrlParams);

  const shouldRenderTimeSeriesSection = !!alert && alert.parsedTriggerExpressions.length > 0;

  return (
    <Page docTitle="Alert">
      <TabbedPageHeader
        title="Alert Details"
        hasBackButton
        headerItems={[<AlertDetailsToolbar key="AlertDetailsPage__AlertDetailsToolbar" alert={alert} />]}
        tabs={[]}
        className="AlertDetailsPage__TabbedPageHeader"
      />

      {shouldShowFirstSection && (
        <div className="AlertDetailsPage__section">
          <div className="AlertDetailsPage__sectionHeader">
            <h3 className="SectionHeader" data-test-id={ELEMENT.alertDetailsDescription}>
              {alert.description}
            </h3>
          </div>
          <div className="AlertDetailsPage__MuteDate" data-test-id={ELEMENT.alertDetailsMuteDate}>
            {alert.silencedUntilNs ? 'Muted until ' + getMuteDate() : null}
          </div>
          <div>{alert.silenceComment ? alert.silenceComment : null}</div>
        </div>
      )}

      <div className="AlertDetailsPage__section">
        <div className="AlertDetailsPage__sectionHeader">
          <h3 className="SectionHeader">Trigger Expression</h3>
        </div>
        <div data-test-id={ELEMENT.alertDetailsTriggerExpression}>{alert.trigger}</div>
      </div>

      {shouldRenderTimeSeriesSection && (
        <div className="AlertDetailsPage__section">
          <div className="AlertDetailsPage__sectionHeader">
            <h3 className="SectionHeader">Time Series</h3>
            <Link href={expandTimeSeriesLink} testId={ELEMENT.alertDetailsSearchLink}>
              View in Search
            </Link>
          </div>
          <TimeSeriesGraphContainer alert={alert} />
        </div>
      )}

      <div className="AlertDetailsPage__section">
        <div className="AlertDetailsPage__sectionHeader">
          <h3 className="SectionHeader">Notifications</h3>
        </div>

        <ul>
          <li>Send alert notifications to:</li>
          <div data-test-id={ELEMENT.alertDetailsEmailAddresses}>{alert.alertAddresses}</div>
          <li data-test-id={ELEMENT.alertDetailsGraceNotification}>{gracePeriodNotification}</li>
          <li data-test-id={ELEMENT.alertDetailsReminderNotification}>{reminderPeriodNotification}</li>
          <li data-test-id={ELEMENT.alertDetailsResolutionNotification}>{resolutionDelayNotification}</li>
        </ul>
      </div>
    </Page>
  );
}

export default AlertDetailsPage;
